import React, { memo, useCallback, useMemo, useRef } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import type { ProductListItemModel } from 'autogen/swagger/all';
import PriceModule from 'components/generic/PriceModule';
import { mapProductListPriceInfoToPriceModuleProps } from 'components/generic/PriceModule/components/shared/mapToPriceModuleProps';
import ProductActionRenderer from 'components/generic/productCTA/ProductActionRenderer';
import { compactActionComponents } from 'components/generic/productCTA/shared/productActionComponents';
import type { IProductActionButton } from 'components/generic/productCTA/shared/types';
import ProductFavoriteButton from 'components/generic/productFavoriteButton';
import ProductImage from 'components/generic/productImage';
import ProductName from 'components/generic/productName';
import RatingStars from 'components/generic/ratingStars';
import { Typography } from 'components/generic/typography';
import useThemeHandler from 'components/shared/productItem/themes/useThemeHandler';
import useAdformImpressionTracking from 'hooks/tracking/useAdformImpressionTracking';
import useIsAuthenticated from 'hooks/user/useIsAuthenticated';
import useRequestContext from 'hooks/user/useRequestContext';
import { verticalEllipsis } from 'modules/helpers/mixins';
import { firstCharacterToLower } from 'modules/helpers/strings';
import type { ThemeType } from '../productList/shared/types';
import LinkAction from '../Routing/components/LinkAction';
import SponsoredSplash, { sponsoredSplashHeight } from '../Splash/components/SponsoredSplash/SponsoredSplash';
import ProductItemDebugger from './components/productDebug/ProductItemDebugger';
import VariantRenderer from './components/Variant/VariantRenderer';
import SplashOverlay from './components/VerticalProductItem/components/SplashOverlay';
import serverSideTracking, { createProductItemTrackingContext } from './shared/helpers';
import { ProductCard, ProductCardBottom, ProductCardMid, ProductCardTop, ProductContent } from './shared/styles';
import { TrackingEvent } from './shared/types';

/* #region  styles */
const Wrapper = styled.div`
    display: flex;
    flex-direction: column;

    ${({ theme }) => (theme as any).Wrapper}
`;

const AnchorOverlay = styled(LinkAction)`
    position: absolute;
    width: 100%;
    height: 100%;

    && {
        z-index: 2;
    }

    &:focus {
        outline: none;
    }
`;

const StyledProductCard = styled(ProductCard)`
    position: relative;
    height: 100%;
    border-radius: 0;
    content-visibility: auto;

    a,
    button,
    label {
        z-index: 3;
    }

    &:has(${AnchorOverlay}:focus-visible) {
        outline: ${({ theme }) => theme.Spacing4} auto ${({ theme }) => theme.ColorBorderFocusRing};
        outline-offset: ${({ theme }) => theme.Spacing4};
        border-radius: ${({ theme }) => theme.RadiusMedium};
    }
`;

const StyledProductName = styled(ProductName)<{ $isChanel?: boolean }>`
    ${({ $isChanel }) =>
        $isChanel &&
        css`
            & > * {
                font-family: Arial, Helvetica, sans-serif;
                font-weight: bold;

                &:last-child {
                    font-weight: normal;
                }
            }
        `}

    ${({ theme }) => (theme as any).StyledProductName}
`;

const StyledPriceModule = styled(PriceModule)`
    justify-items: start;
    text-align: left;
    align-content: end;
`;

const StyledProductActionRenderer = styled(ProductActionRenderer)`
    align-self: end;
`;

const VariantAndFavorite = styled.div`
    display: grid;
    grid-template-columns: 1fr 40px;
    grid-template-areas: 'Variant Favorite';
    align-items: center;
    gap: ${({ theme }) => theme.Spacing4};
`;

const StyledProductFavoriteButton = styled(ProductFavoriteButton)`
    grid-area: Favorite;
`;

const StyledSponsoredSplash = styled(SponsoredSplash)`
    margin-top: -${sponsoredSplashHeight}; 
`;

const StyledRatingStars = styled(RatingStars)`
    padding-top: ${({ theme }) => (theme).Spacing4};
`;

/* #endregion */

type ProductItemProps = {
    model: ProductListItemModel;
    recommendationPlacement?: string;
    eagerLoad?: boolean;
    themeType?: ThemeType;
    pageContext?: 'ProductList';
};

const ProductItem = React.forwardRef<HTMLDivElement, ProductItemProps>(
    (
        {
            model,
            recommendationPlacement,
            themeType,
            eagerLoad = false,
            pageContext,
            ...restProps
        }: ProductItemProps & React.HTMLAttributes<HTMLDivElement>,
        ref
    ) => {
        const {
            /* ----------------General Product Item properties-------------*/
            id: productId,
            externalProductId,
            linkAction,
            primaryImage,
            splashes,
            priceInfo: { minimumSalesQuantity },
            priceInfo,
            showPossibleAdditionalDeliveryCost,
            favoriteStatus: { favoriteStatus },
            standardName, // name of product
            listName, // Name, Brand Desc, AmountAndUnit
            /* ----------------For Review Module-------------*/
            averageRating,
            showReviews,
            reviewCount,
            /* ----------------For Product Action-------------*/
            action, // actionType for the CTA
            variants, // VariantsInfo
            isSubscribable,
            isSponsored,
            isSubscriptionContext,
            addToBasketType,
            requireLoginForCheckout,
            fromPowerstep, // Whether the CTA was clicked from a powerstep only relevant for add to basket button
            showPowerstepOnAddToBasket, // Whether the CTA should open powerstep or not, only relevent for add to basket button
            bundleInfo, // Used when adding a bundle to basket, adds the params to the body of the req ( also used for abit of tracking )
            /* -----------------Tracking related-----------*/
            dataLayerDto,
            dataLayerDto: { listName: dataLayerListName },
            uaTrackingCategory,
            uaTrackingLabel,
            trackModel,
            /* ----------------- Debug -----------*/
            explainedScore,
            useCustomChanelStyling,
            snippet
        } = model;
        const isAuthenticated = useIsAuthenticated();

        const { posRequest } = useRequestContext();

        const trackingRef = useRef(null);

        const trackingCategory = firstCharacterToLower(uaTrackingCategory);

        const trackingContext = useMemo(
            () =>
                createProductItemTrackingContext(
                    trackingCategory,
                    uaTrackingLabel,
                    trackModel,
                    bundleInfo,
                    recommendationPlacement
                ),
            [trackingCategory, uaTrackingLabel, trackModel, bundleInfo, recommendationPlacement]
        );
        const productActionButtonModel: IProductActionButton['model'] = useMemo(
            () => ({
                action,
                trackingContext,
                productId,
                externalProductId,
                productNavigationLink: linkAction,
                isSubscriptionContext,
                isSubscribable,
                minimumSalesQuantity,
                bundleInfo,
                listName: dataLayerListName,
                fromPowerstep,
                showPowerstepOnAddToBasket,
                addToBasketType,
                requireLoginForCheckout,
                recommendationPlacement,
                variants,
                customerNotification: {
                    image: model.primaryImage.url,
                    isPreorder: false,
                    productId,
                    productName: model.listName
                }
            }),
            [
                action,
                trackingContext,
                productId,
                externalProductId,
                linkAction,
                isSubscriptionContext,
                isSubscribable,
                minimumSalesQuantity,
                bundleInfo,
                dataLayerListName,
                fromPowerstep,
                showPowerstepOnAddToBasket,
                addToBasketType,
                requireLoginForCheckout,
                recommendationPlacement,
                variants,
                model.primaryImage.url,
                model.listName
            ]
        );

        useAdformImpressionTracking({
            ref: trackingRef,
            impTrackingUrls: trackModel?.adformProductTrackingModel?.impressionTrackingUrls
        });

        const { ButtonStyling, ...theme } = useThemeHandler(themeType) as any;
        const stringifiedTrackModel = useMemo(() => JSON.stringify(trackModel), [trackModel]);
        const stringifiedDataLayerModel = useMemo(() => JSON.stringify(dataLayerDto), [dataLayerDto]);

        const algonomyTracking = useCallback(
            (trackingEvent: TrackingEvent) => {
                if (trackModel) {
                    serverSideTracking(stringifiedTrackModel, trackingEvent);
                }
            },
            [stringifiedTrackModel, trackModel]
        );
        const showFavoriteButton = isAuthenticated || !posRequest?.isPosRequest;

        // Memoize click handlers for better performance
        const handleProductClick = useCallback(() => {
            algonomyTracking(TrackingEvent.ProductClick);
        }, [algonomyTracking]);

        const handleFavoriteClick = useCallback(() => {
            algonomyTracking(TrackingEvent.AddToFavourites);
        }, [algonomyTracking]);

        const priceModuleProps = mapProductListPriceInfoToPriceModuleProps(priceInfo);

        return (
            <ThemeProvider theme={theme}>
                <Wrapper {...restProps}>
                    {pageContext === 'ProductList' && isSponsored && <StyledSponsoredSplash productId={productId} />}{' '}
                    <StyledProductCard
                        size={{ xs: 'xs', sm: 'sm', xl: 'md' }}
                        ref={ref}
                        data-datalayer={stringifiedDataLayerModel}
                        onClick={handleProductClick}
                    >
                        <ProductItemDebugger explainedScore={explainedScore} />
                        <AnchorOverlay action={linkAction} aria-label={model.standardName} ref={trackingRef} />

                        <ProductContent>
                            {/* Top Content */}
                            <ProductCardTop $isChanel={useCustomChanelStyling}>
                                {/* ProductImage - Displays the product image  */}
                                <ProductImage
                                    image={{
                                        ...primaryImage,
                                        alt: standardName
                                    }}
                                    aria-label={standardName}
                                    width={245}
                                    maxWidth="245px"
                                    transformType="SquareScale"
                                    loading={eagerLoad ? 'eager' : 'lazy'}
                                />
                                <SplashOverlay splashes={splashes} />
                            </ProductCardTop>

                            {/* Mid Content */}
                            <ProductCardMid>
                                <VariantAndFavorite>
                                    <VariantRenderer variants={variants} />

                                    {showFavoriteButton && (
                                        <StyledProductFavoriteButton
                                            variant="favoriteGhost"
                                            initialFavoriteState={favoriteStatus === 'IsFavorite'}
                                            iconSize="lg"
                                            productId={productId}
                                            onClick={handleFavoriteClick}
                                            uaTrackingCategory={trackingCategory}
                                        />
                                    )}
                                </VariantAndFavorite>

                                <StyledProductName
                                    productName={listName}
                                    size="md"
                                    snippet={snippet}
                                    $isChanel={useCustomChanelStyling}
                                />

                                {showReviews && reviewCount > 0 && (
                                    <StyledRatingStars
                                        rating={averageRating}
                                        reviewCount={reviewCount}
                                        showAvgRatingCount={false}
                                    />
                                )}
                            </ProductCardMid>

                            {/* Bottom Content */}
                            <ProductCardBottom>
                                {/* PriceModule - Displays everything related to price */}
                                <StyledPriceModule
                                    showPossibleAdditionalDeliveryCost={showPossibleAdditionalDeliveryCost}
                                    size="sm"
                                    {...priceModuleProps}
                                />

                                {/* ProductAction - Displays the current product action button */}
                                <ThemeProvider theme={{ ButtonStyling } as any}>
                                    <StyledProductActionRenderer
                                        overriddenActionComponents={compactActionComponents}
                                        model={productActionButtonModel}
                                        css={css`
                                            flex: 1;
                                        `}
                                    />
                                </ThemeProvider>
                            </ProductCardBottom>
                        </ProductContent>
                    </StyledProductCard>
                </Wrapper>
            </ThemeProvider>
        );
    }
);

// Custom comparison function for memo optimization
const ProductItemMemoComparison = (prevProps: ProductItemProps, nextProps: ProductItemProps) => {
    // Only re-render if essential props have changed
    return (
        prevProps.model.id === nextProps.model.id &&
        prevProps.model.priceInfo === nextProps.model.priceInfo &&
        prevProps.model.favoriteStatus === nextProps.model.favoriteStatus &&
        prevProps.model.averageRating === nextProps.model.averageRating &&
        prevProps.model.reviewCount === nextProps.model.reviewCount &&
        prevProps.themeType === nextProps.themeType &&
        prevProps.eagerLoad === nextProps.eagerLoad &&
        prevProps.pageContext === nextProps.pageContext
    );
};

export default memo(ProductItem, ProductItemMemoComparison);
